import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import QuestionSubmission from './QuestionSubmission';
import { getUserRatingHistory, updateProfilePhoto } from '../Helpers/firebaseHelpers';
import AdminPanel from './AdminPanel';
import WriteBlog from './WriteBlog';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebase-config";
import DefaultPFP from "../assets/EE_Logo.png";
import { db } from "../firebase-config";
import { useAuthUser } from './useAuthUser';

const Profile = ({ Std }) =>
{
  const { userAuth } = useAuthUser();
  const { username } = useParams();

  const [currentView, setCurrentView] = useState('profile');
  const [ratingHistory, setRatingHistory] = useState([]);
  const [checkingUser, setCheckingUser] = useState(true);
  const [userExists, setUserExists] = useState(false);
  const [loadingRatings, setLoadingRatings] = useState(true);
  const [photoURL, setPhotoURL] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const [SearchUsername, setSearchUsername] = useState('');
  const navigate = useNavigate();

  const isAuthorized = () =>
  {
    const authorizedAdmins = ['TanishqAC'];
    return authorizedAdmins.includes(username);
  };

  /* ---------------- USER CHECK ---------------- */

  useEffect(() =>
  {
    const checkUsername = async () =>
    {
      const refDoc = doc(db, "users", username);
      const snap = await getDoc(refDoc);

      if (snap.exists())
      {
        setUserExists(true);
        setPhotoURL(snap.data().photoURL || null);
      }
      else
      {
        setUserExists(false);
      }
      setCheckingUser(false);
    };

    checkUsername();
  }, [username]);

  /* ---------------- RATING HISTORY ---------------- */

  useEffect(() =>
  {
    const fetchRatingHistory = async () =>
    {
      setLoadingRatings(true);
      const history = await getUserRatingHistory(username);

      const chartData = history.map(entry => ({
        name: entry.ContestID,
        rating: entry.Rating,
      }));

      if (chartData.length === 0)
      {
        chartData.push({ name: 'Start', rating: 0 });
      }

      setRatingHistory(chartData);
      setLoadingRatings(false);
    };

    fetchRatingHistory();
  }, [username]);

  const CustomTooltip = ({ active, payload }) =>
  {
    if (active && payload && payload.length)
    {
      const data = payload[0].payload;
      return (
        <div className="bg-gray-800 border border-gray-700 p-3 rounded shadow-lg">
          <p className="text-blue-400">Rating: {data.rating}</p>
          <p className="text-green-400">Contest ID: {data.name}</p>
        </div>
      );
    }
    return null;
  };

  /* ---------------- LOAD STATES ---------------- */
  
  if (!userAuth)
  {
    return (
      <div className="mt-20 text-center text-xl sm:text-4xl">
        Loading...
      </div>
    );
  }

  if (checkingUser)
  {
    return (
      <div className="mt-20 text-center text-xl sm:text-4xl">
        Checking username…
      </div>
    );
  }

  if (!userExists)
  {
    return (
      <div className="mt-20 text-center text-xl sm:text-4xl">
        No such username exists.
      </div>
    );
  }

  if (loadingRatings)
  {
    return (
      <div className="mt-20 text-center text-xl sm:text-4xl">
        Loading user data…
      </div>
    );
  }

  if (currentView === 'writing')
  {
    return <WriteBlog />;
  }

  /* ---------------- PROFILE PIC HANDLERS ---------------- */

  const handleProfilePicUpload = async (file) =>
  {
    if (!file || !file.type.startsWith("image/") || file.size > 8 * 1024 * 1024)
      return;

    setUploadingPhoto(true);

    try
    {
      const imageRef = ref(storage, `profilePictures/${userAuth.uid}`);
      await uploadBytes(imageRef, file, { contentType: file.type });
      const url = await getDownloadURL(imageRef);

      await updateProfilePhoto(url);
      setPhotoURL(url);
    }
    catch (err)
    {
      console.error(err);
    }

    setUploadingPhoto(false);
  };

  const handleRemoveProfilePic = async () =>
  {
    if (!photoURL) return;

    setUploadingPhoto(true);

    try
    {
      const imageRef = ref(storage, `profilePictures/${userAuth.uid}`);
      await deleteObject(imageRef);
      await updateProfilePhoto(null);
      setPhotoURL(null);
    }
    catch (err)
    {
      console.error(err);
    }

    setUploadingPhoto(false);
  };

  const userRating =
    ratingHistory.length > 0
      ? ratingHistory[ratingHistory.length - 1].rating
      : 0;

  const handleFindUsername = () => 
  {
    if (!SearchUsername.trim())
    {
      return;
    }
    navigate(`/profile/${SearchUsername}`);
    setSearchUsername('');
  };

  /* ================= PROFILE VIEW ================= */

  if (currentView === 'profile')
  {
    return (
      <div className="p-4 sm:p-8">
        <h2 className="text-2xl sm:text-3xl font-bold mb-6">
          User Profile
        </h2>

        <div className="rounded-xl shadow-md p-4 sm:p-6 border border-gray-700 space-y-6">

          {/* HEADER */}
          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
            <div className="flex flex-col items-center space-y-2">
              <img
                src={photoURL || DefaultPFP}
                className="w-40 h-40 sm:w-52 sm:h-52 rounded-2xl object-cover border border-gray-400"
              />

              {(username === userAuth.displayName) && (
                <div className="flex gap-2">
                  <label className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-4 py-1 rounded cursor-pointer">
                    Edit
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={(e) =>
                        handleProfilePicUpload(e.target.files[0])
                      }
                    />
                  </label>

                  {photoURL && (
                    <button
                      onClick={handleRemoveProfilePic}
                      className="bg-gray-600 hover:bg-gray-700 text-white text-sm px-4 py-1 rounded"
                    >
                      Unset
                    </button>
                  )}
                </div>
              )}

              {uploadingPhoto && (
                <p className="text-sm">Updating profile picture…</p>
              )}
            </div>

            <div className="text-center sm:text-left">
              <h3 className="text-2xl sm:text-3xl font-semibold">
                {username}
              </h3>
              <p className="text-lg">
                Standard: <span className="font-bold">{Std}</span>
              </p>
              <p className="text-lg">
                Rating: <span className="font-bold">{userRating}</span>
              </p>
            </div>
          </div>

          {/* RATING GRAPH */}
          <div className="border-t border-gray-700 pt-6">
            <h4 className="text-xl font-semibold mb-4">
              Rating History
            </h4>

            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={ratingHistory}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis domain={[0, 'auto']} />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="monotone"
                  dataKey="rating"
                  stroke="#63b3ed"
                  strokeWidth={2}
                  activeDot={{ r: 8 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* ACTION BUTTONS — ALWAYS ONE ROW */}
          {(username === userAuth.displayName) && (
            <div className="border-t border-gray-700 pt-6">
              <div className="flex flex-wrap justify-center gap-4">

                <button
                  onClick={() => setCurrentView('writing')}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg"
                >
                  Write a Blog
                </button>

                {!isAuthorized() && (
                  <button
                    onClick={() => setCurrentView('submit-question')}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
                  >
                    📝 Send us a question
                  </button>
                )}

                {isAuthorized() && (
                  <button
                    onClick={() => setCurrentView('admin-panel')}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg font-medium"
                  >
                    🔧 Admin Panel
                  </button>
                )}

              </div>
            </div>
          )}
        </div>

        <div className=" w-full max-w-xl mx-auto flex flex-col mt-2 sm:mt-4 sm:flex-row gap-3 ">
          <span className="mt-[8px] text-xl"> Find User :</span> 
          <input
            type="text"
            value={SearchUsername}
            onChange={(e) => setSearchUsername(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleFindUsername()}
            placeholder="Enter username"
            className=" flex-1 px-4 py-3 text-base border border-gray-500 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
          />
            <button
              onClick={handleFindUsername}
              disabled={!SearchUsername.trim()}
              className=" px-6 py-3 ml-12 mr-12 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-500 disabled:bg-blue-300 disabled:cursor-not-allowed transition "
            >
              Find Username
          </button>
        </div>
      </div>
    );
  }

  /* ================= OTHER VIEWS ================= */

  return (
    <div className="p-4">
      <button
        onClick={() => setCurrentView('profile')}
        className="mb-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
      >
        ← Back to Profile
      </button>

      {currentView === 'submit-question'
        ? <QuestionSubmission />
        : <AdminPanel />
      }
    </div>
  );
};

export default Profile;